<template>
    <!-- RÉCAPITULATIF — panneau PLEINE LARGEUR, affiché dans le corps du modal à la place de
         la liste des articles. Ce n'est ni un popover ni une infobulle : il occupe toute la
         zone principale, comme le tableau d'un devis.

         > [!warning] Ce panneau n'enregistre rien.
         > Il affiche la composition en cours. Le seul chemin d'écriture reste le bouton
         > « Ajouter au devis », dans le footer. -->
    <div class="summary-view">
        <div class="summary-view-head">
            <span class="ppv2-text-black ppv2-text-base poppinsBold">{{ $t('devis.v3.summary_title') }}</span>
            <button type="button" class="summary-close" :aria-label="$t('devis.v3.summary_close')" @click="$emit('close')">
                <X :size="20" />
            </button>
        </div>

        <div class="summary-table-wrap custom-scrollbar">
            <table class="summary-table">
                <thead>
                    <tr>
                        <th class="col-article">{{ $t('devis.v3.summary_col_article') }}</th>
                        <th class="col-qty">{{ $t('devis.v3.summary_col_qty') }}</th>
                        <th class="col-unit">{{ $t('devis.v3.summary_col_unit_ht') }}</th>
                        <th class="col-vat">{{ $t('devis.v3.tva') }}</th>
                        <th class="col-total">{{ $t('devis.v3.summary_col_total') }}</th>
                        <th class="col-actions"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="line in lines" :key="line.key">
                        <td class="col-article">
                            <div class="summary-article">
                                <div class="summary-thumb" :class="'is-' + line.type">
                                    <img v-if="line.image" :src="line.image" :alt="line.name" />
                                </div>
                                <div class="summary-article-text">
                                    <span class="summary-article-name poppinsMedium">{{ line.name }}</span>
                                    <span v-if="line.meta.length > 0" class="summary-article-meta">{{
                                        line.meta.join(' · ')
                                    }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="col-qty">&times;{{ line.qty }}</td>
                        <td class="col-unit">{{ line.qty > 0 ? formatEuro(line.totalHt / line.qty) : '—' }}</td>
                        <!-- Un taux nul, ou mêlé sur un billet groupé, n'a pas de pourcentage à
                             montrer : un tiret est plus honnête qu'un « 0 % » trompeur. -->
                        <td class="col-vat">{{ line.vatRate ? formatRate(line.vatRate) : '—' }}</td>
                        <td class="col-total poppinsSemiBold">{{ formatEuro(line.totalHt) }}</td>
                        <td class="col-actions">
                            <div class="summary-row-actions">
                                <!-- Le crayon n'apparaît qu'au survol : il ramène LÀ OÙ la
                                     ligne a été composée, geste secondaire à côté du retrait. -->
                                <button
                                    type="button"
                                    class="summary-row-btn summary-row-btn--edit"
                                    :title="$t('devis.v3.summary_edit_line')"
                                    :aria-label="$t('devis.v3.summary_edit_line')"
                                    @click="$emit('edit', line)"
                                >
                                    <Pencil :size="15" />
                                </button>
                                <button
                                    type="button"
                                    class="summary-row-btn summary-row-btn--remove"
                                    :title="$t('devis.v3.remove_line')"
                                    :aria-label="$t('devis.v3.remove_line')"
                                    @click="$emit('remove', line)"
                                >
                                    <X :size="15" />
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="summary-totals">
            <div class="summary-total-row">
                <span>{{ $t('devis.v3.summary_subtotal_ht') }}</span>
                <span class="summary-amount">{{ formatEuro(totalHt) }}</span>
            </div>
            <div class="summary-total-row">
                <span>{{ $t('devis.v3.total_ht') }}</span>
                <span class="summary-amount">{{ formatEuro(totalHt) }}</span>
            </div>
            <div v-for="group in vatGroups" :key="group.rate" class="summary-total-row summary-total-row--muted">
                <span
                    >{{ $t('devis.v3.tva') }} {{ formatRate(group.rate) }} {{ $t('devis.v3.summary_vat_on') }}
                    {{ formatEuro(group.baseHt) }}</span
                >
                <span class="summary-amount">{{ formatEuro(group.amount) }}</span>
            </div>
            <div class="summary-total-row summary-total-row--grand poppinsBold">
                <span>{{ $t('devis.v3.total_ttc') }}</span>
                <span class="summary-amount">{{ formatEuro(totalTtc) }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { X, Pencil } from 'lucide-vue'

export default {
    name: 'SelectionSummary',
    components: { X, Pencil },
    props: {
        /**
         * Lignes déjà normalisées par le stepper — le composant AFFICHE, il ne calcule pas.
         * Forme : { key, type, name, image, qty, vatRate, totalHt, totalTtc, meta: string[] }
         */
        lines: { type: Array, default: () => [] },
        totalHt: { type: Number, default: 0 },
        totalTtc: { type: Number, default: 0 },
        /** Ventilation par taux : [{ rate, baseHt, amount }] */
        vatGroups: { type: Array, default: () => [] },
    },
    methods: {
        formatEuro(value) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0)
        },
        // Un taux entier s'affiche sans décimale, un taux à virgule la garde : « 20 % » et
        // « 5,5 % », jamais « 20,0 % ».
        formatRate(rate) {
            return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(rate) || 0) + ' %'
        },
    },
}
</script>

<style scoped>
/* Le panneau occupe TOUTE la zone principale du modal, en largeur comme en hauteur : le
   tableau défile en son sein et les totaux restent visibles en bas, plutôt que de partir
   au fil du défilement. `.stepper-body-v3` ne pose aucun padding, c'est donc ici.
   Si la hauteur du parent ne se résout pas, le panneau retombe sur la hauteur de son
   contenu et c'est le corps du modal qui défile — dégradé, jamais cassé. */
.summary-view {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 18px 24px 16px;
}

.summary-view-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 14px;
    border-bottom: 1px solid #eef1f5;
}

.summary-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    color: #1d1d1b;
    opacity: 0.55;
    cursor: pointer;
    transition:
        background 0.18s ease,
        opacity 0.18s ease;
}

.summary-close:hover {
    background: #f4f6f9;
    opacity: 1;
}

.summary-table-wrap {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.summary-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 8px;
    background: #fff;
    border-bottom: 1px solid #eef1f5;
    color: #1d1d1b;
    opacity: 0.5;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: right;
    white-space: nowrap;
}

.summary-table thead th.col-article {
    text-align: left;
}

.summary-table tbody td {
    padding: 12px 8px;
    border-bottom: 1px solid #f4f6f9;
    text-align: right;
    vertical-align: middle;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.summary-table tbody td.col-article {
    text-align: left;
    white-space: normal;
    width: 100%;
}

.summary-article {
    display: flex;
    align-items: center;
    gap: 12px;
}

.summary-thumb {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #f2f4f8;
    overflow: hidden;
}

/* Pastille de type quand l'article n'a pas d'image : la vignette reste, teintée. */
.summary-thumb.is-experience {
    background: rgba(125, 45, 189, 0.12);
}

.summary-thumb.is-resto {
    background: rgba(232, 145, 45, 0.14);
}

.summary-thumb.is-option {
    background: rgba(45, 155, 232, 0.14);
}

.summary-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.summary-article-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.summary-article-name {
    color: #1d1d1b;
}

.summary-article-meta {
    color: #1d1d1b;
    opacity: 0.5;
    font-size: 11.5px;
}

.summary-table tbody td.col-actions {
    padding-left: 4px;
    padding-right: 0;
    width: 1%;
}

.summary-row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
}

.summary-row-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    color: #1d1d1b;
    opacity: 0.45;
    cursor: pointer;
    transition:
        opacity 0.15s ease,
        background 0.15s ease,
        color 0.15s ease;
}

.summary-row-btn:hover {
    background: #f2f4f8;
    opacity: 1;
}

.summary-row-btn--remove:hover {
    background: rgba(214, 60, 60, 0.1);
    color: #d63c3c;
}

/* Le crayon ne se montre qu'au survol de la ligne ; la croix, elle, reste toujours là. */
.summary-row-btn--edit {
    opacity: 0;
    pointer-events: none;
}

.summary-table tbody tr:hover .summary-row-btn--edit,
.summary-row-btn--edit:focus-visible {
    opacity: 0.45;
    pointer-events: auto;
}

.summary-table tbody tr:hover .summary-row-btn--edit:hover {
    opacity: 1;
}

.summary-totals {
    flex: 0 0 auto;
    padding: 14px 8px 2px;
    border-top: 1px solid #eef1f5;
}

.summary-total-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 5px 0;
    font-size: 13px;
    color: #1d1d1b;
}

.summary-total-row--muted {
    opacity: 0.62;
    font-size: 12.5px;
}

.summary-total-row--grand {
    margin-top: 6px;
    padding-top: 12px;
    border-top: 1px solid #eef1f5;
    font-size: 15px;
}

.summary-amount {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

@media (max-width: 720px) {
    .summary-table {
        font-size: 12px;
    }

    .summary-thumb {
        width: 32px;
        height: 32px;
    }
}
</style>
